import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { usePostResetPwApi } from '@/api/auth';
import { PrimaryButton } from '@/components/common/Button/style';
import Field from '@/components/common/Field/Field';
import InputText from '@/components/common/Input/InputText/InputText';
import { useModalStore } from '@/store/modalStore';
import { useToastStore } from '@/store/toastStore';

import * as S from './style';

interface RegisterField {
	password: string;
	passwordCheck: string;
}
const ModifyPw = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const member_id = location.state?.member_id;
	const { openToast } = useToastStore();
	const { showModal } = useModalStore();

	useEffect(() => {
		if (!member_id) {
			showModal(t('잘못된 접근입니다.'));
			navigate('/auth/login', { replace: true });
		}
	}, [member_id, navigate, showModal, t]);

	const userRegisterSchema = z
		.object({
			password: z
				.string()
				.min(1, { message: t('6~20자의 영문, 숫자,특수문자만 입력 가능합니다.') })
				.min(6, { message: t('6~20자의 영문, 숫자,특수문자만 입력 가능합니다.') })
				.max(20, { message: t('6~20자의 영문, 숫자,특수문자만 입력 가능합니다.') })
				.regex(/^[A-Za-z0-9!@#$%^&*()-_+=]+$/, { message: t('6~20자의 영문, 숫자,특수문자만 입력 가능합니다.') }),
			passwordCheck: z.string().min(1, { message: t('비밀번호가 일치하지 않습니다.') }),
		})
		.refine((data) => data.password === data.passwordCheck, {
			message: t('비밀번호가 일치하지 않습니다.'),
			path: ['passwordCheck'],
		});

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		defaultValues: {
			password: '',
			passwordCheck: '',
		},
	});
	const { mutateAsync: resetPw } = usePostResetPwApi();
	const onClickButton = async () => {
		const res = await resetPw({
			member_id: member_id,
			member_password: watch('password'),
		});
		if (res.data.success) {
			openToast({
				subContent: t('한국응용과학기술학회'),
				content: t('비밀번호가 변경되었습니다.'),
			});
			navigate('/auth/login', { replace: true });
		} else {
			showModal(t('현재 사용중인 비밀번호와 동일합니다.\n다른 비밀번호로 다시 입력해주세요.'));
		}
	};

	return (
		<S.Page>
			<S.Container>
				<S.PageHeader>
					<S.Eyebrow>Account</S.Eyebrow>
					<S.Title>{t('비밀번호 변경')}</S.Title>
					<S.Subtitle>{t('auth.modifyPw.subtitle')}</S.Subtitle>
				</S.PageHeader>

				<S.FormCard>
					<S.InnerForm onSubmit={handleSubmit(onClickButton)} noValidate>
					<S.FieldBox>
						<Field title="signup.basicInfo.fields.password" error={errors.password?.message ?? ''}>
							<InputText
								isError={!!errors.password}
								{...register('password')}
								type="password"
								placeholder="common.placeholders.password"
							/>
						</Field>
						<Field title="signup.basicInfo.fields.passwordConfirm" error={errors.passwordCheck?.message ?? ''}>
							<InputText
								type="password"
								isError={!!errors.passwordCheck}
								placeholder="common.placeholders.passwordConfirm"
								{...register('passwordCheck')}
							/>
						</Field>
						<S.FormActions>
							<PrimaryButton type="submit">{t('common.actions.completeUpdate')}</PrimaryButton>
						</S.FormActions>
					</S.FieldBox>
					</S.InnerForm>
				</S.FormCard>
			</S.Container>
		</S.Page>
	);
};
export default ModifyPw;
